/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
  SupportedLanguage,
  LanguageConfig,
  LANGUAGE_STORAGE_KEY,
} from "i18n/types";
import { Button, FontAwesomeIcon } from "..";
import { pushToDataLayer } from "utils/gtm";
import useDetectClickOutside from "utils/useDetectClickOutside";

interface LanguageSwitcherProps {
  className?: string;
  fullWidth?: boolean;
}

// Helper function to normalize language code (e.g., en-US -> en)
const normalizeLanguage = (lang: string): SupportedLanguage => {
  const languageCode = lang?.split("-")[0].toLowerCase();

  // Check if it's a supported language
  if (
    Object.values(SupportedLanguage).includes(languageCode as SupportedLanguage)
  ) {
    return languageCode as SupportedLanguage;
  }

  // Default to English if not supported
  return SupportedLanguage.EN;
};

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
  fullWidth,
}) => {
  const { i18n } = useTranslation<any>();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useDetectClickOutside(dropdownRef, () => setIsOpen(false));

  // Normalize the current language to handle locale codes like en-US
  const currentLanguage = normalizeLanguage(i18n.language);
  const handleLanguageChange = async (lang: SupportedLanguage) => {
    try {
      if (lang === SupportedLanguage.EN) {
        pushToDataLayer({
          event: "select_en",
          screen_name: "header",
          journey_step: "header",
        });
      } else if (lang === SupportedLanguage.AR) {
        pushToDataLayer({
          event: "select_ar",
          screen_name: "header",
          journey_step: "header",
        });
      }

      // await i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      setIsOpen(false);
      // Force page reload to ensure all components update properly
      window.location.reload();
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  // ...

  return (
    <div className={cn("relative inline-block", className)} ref={dropdownRef}>
      <Button
        variant="tertiary"
        size="md"
        fullWidth={fullWidth}
        onClick={() => {
          pushToDataLayer({
            event: "lang_icon_click",
            screen_name: "header",
            journey_step: "header",
          });
          setIsOpen(!isOpen);
        }}
        className={cn("!py-2 !px-3 ", className)}
        aria-label="Select Language"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <FontAwesomeIcon name="fa-globe" size="sm" />
          <span className="text-xs leading-none">
            {LanguageConfig[currentLanguage].name}
          </span>
          <FontAwesomeIcon
            name={isOpen ? "fa-chevron-up" : "fa-chevron-down"}
            size="xs"
            className="-text--theme-colors-text-body"
          />
        </div>
      </Button>

      {isOpen && (
        <div className="absolute top-full mt-2 -rounded-button -bg--theme-colors-menu-nav-bg border -border--theme-colors-action-tertiary-default-border shadow-lg z-50 start-0 w-40">
          {Object.entries(LanguageConfig).map(([key, config]) => {
            const lang = key as SupportedLanguage;
            const isSelected = lang === currentLanguage;

            return (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={cn(
                  "w-full flex items-center justify-between gap-3 px-5 py-3 text-sm transition-colors",
                )}
              >
                <span
                  className={cn(
                    "text-sm leading-none -text--theme-colors-text-title",
                  )}
                >
                  {config.menuName}
                </span>

                {isSelected && (
                  <svg
                    className={cn(
                      "w-5 h-5 -text--theme-colors-text-title font-bold",
                    )}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
